import type {
  AnalyticsCheckoutsResponse,
  AnalyticsResponse,
} from "@/@types/Analytics";
import {
  convertToVisualParams,
  type FilterParams,
  type KeyLabel,
} from "@/hooks/useSales";
import type { FilterAnalyticsFormData } from "@/pages/Reports/Analytics/Partials/FilterAnalytics";
import type { AnalyticsParams } from "@/services/reports.service";
import { ReportsService } from "@/services/reports.service";
import { SellsService } from "@/services/sells.service";
import useCurrencyStore from "@/store/useCurrencyStore";
import useModalStore from "@/store/useModalStore";
import { getDefaultDate } from "@/utils/dateHelpers";
import { useQuery } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import {
  parseAsArrayOf,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

const ANALYTICS_ROLE_STORAGE_KEY = "analytics_role";
const CHECKOUTS_PER_PAGE = 15;
export const FILTER_MODAL_KEY = "filter-analytics";

export interface FilterBadge {
  key: string;
  group: string;
  label: string;
  onClick: () => void;
}

interface AnalyticsQueryParams {
  checkouts: string[];
  products: string[] | null;
  offers: string[] | null;
  affiliates: string[] | null;
  coproducers: string[] | null;
  templates: string[] | null;
  role: string;
  startDate: string;
  endDate: string;
  currency: string;
}

function extractUuid(value: string): string {
  return value.includes(":::") ? value.split(":::")[1] : value;
}

function extractUuids(values: string[] | null | undefined): string[] | undefined {
  if (!values?.length) return undefined;
  const uuids = values.map(extractUuid).filter(Boolean);
  return uuids.length > 0 ? uuids : undefined;
}

function nonEmpty(values: string[] | null): string[] | undefined {
  return values && values.length > 0 ? values : undefined;
}

function buildAnalyticsParams(
  params: AnalyticsQueryParams,
  pagination?: { page: number; perPage: number },
): AnalyticsParams {
  return {
    start_date: params.startDate,
    end_date: params.endDate,
    currency: params.currency,
    role: params.role,
    ...(pagination ? { page: pagination.page, per_page: pagination.perPage } : {}),
    checkouts: nonEmpty(params.checkouts),
    products: nonEmpty(params.products),
    offers: nonEmpty(params.offers),
    affiliates: nonEmpty(params.affiliates),
    coproducers: nonEmpty(params.coproducers),
    templates: nonEmpty(params.templates),
  };
}

function toFilterValues(options?: { label?: string; value?: string }[]) {
  return options?.map((opt) => `${opt.label}:::${opt.value}`);
}

export function useAnalyticsPanel() {
  const { t } = useTranslation(["sales", "reports", "common"]);
  const currency = useCurrencyStore((s) => s.currency);
  const { openModal } = useModalStore();
  const roleInitialized = useRef(false);

  const [fromDate, setFromDate] = useQueryState(
    "from",
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [toDate, setToDate] = useQueryState(
    "to",
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [role, setRole] = useQueryState(
    "role",
    parseAsString.withDefault("producer"),
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    "products",
    parseAsArrayOf(parseAsString),
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    "offers",
    parseAsArrayOf(parseAsString),
  );
  const [checkoutsFilter, setCheckoutsFilter] = useQueryState(
    "checkouts",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    "affiliates",
    parseAsArrayOf(parseAsString),
  );
  const [coproducersFilter, setCoproducersFilter] = useQueryState(
    "coproducers",
    parseAsArrayOf(parseAsString),
  );
  const [templatesFilter, setTemplatesFilter] = useQueryState(
    "templates",
    parseAsArrayOf(parseAsString),
  );

  const filterSetters = useMemo(
    () => ({
      products: setProductsFilter,
      offers: setOffersFilter,
      affiliates: setAffiliatesFilter,
      coproducers: setCoproducersFilter,
      templates: setTemplatesFilter,
    }),
    [
      setProductsFilter,
      setOffersFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ],
  );

  const filterParams: FilterParams = useMemo(
    () => ({
      role,
      products: productsFilter ?? undefined,
      offers: offersFilter ?? undefined,
      checkouts: checkoutsFilter ?? undefined,
      affiliates: affiliatesFilter ?? undefined,
      coproducers: coproducersFilter ?? undefined,
      templates: templatesFilter ?? undefined,
    }),
    [
      role,
      productsFilter,
      offersFilter,
      checkoutsFilter,
      affiliatesFilter,
      coproducersFilter,
      templatesFilter,
    ],
  );

  const filterVisualParams = useMemo(
    () => convertToVisualParams(filterParams),
    [filterParams],
  );

  const productUuids = useMemo(
    () => extractUuids(productsFilter) ?? [],
    [productsFilter],
  );
  const offerUuids = useMemo(
    () => extractUuids(offersFilter) ?? [],
    [offersFilter],
  );
  const productUuidsKey = productUuids.join(",");
  const offerUuidsKey = offerUuids.join(",");

  const resolveDefaultRole = useCallback(async () => {
    try {
      const soldProducts = await SellsService.getSoldProducts(undefined, currency);
      return soldProducts.length === 0 ? "affiliate" : "producer";
    } catch {
      return "producer";
    }
  }, [currency]);

  useEffect(() => {
    if (roleInitialized.current) return;

    const initRole = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("role")) {
        roleInitialized.current = true;
        return;
      }

      const storedRole = localStorage.getItem(ANALYTICS_ROLE_STORAGE_KEY);
      if (storedRole) {
        setRole(storedRole);
        roleInitialized.current = true;
        return;
      }

      const defaultRole = await resolveDefaultRole();
      setRole(defaultRole);
      localStorage.setItem(ANALYTICS_ROLE_STORAGE_KEY, defaultRole);
      roleInitialized.current = true;
    };

    initRole();
  }, [resolveDefaultRole, setRole]);

  useEffect(() => {
    if (!role) return;
    localStorage.setItem(ANALYTICS_ROLE_STORAGE_KEY, role);
  }, [role]);

  useEffect(() => {
    let active = true;

    const keepAvailable = (available: { value: string }[]) => {
      const ids = new Set(available.map((item) => item.value));
      return (current: string[] | null) => {
        if (!current?.length) return current;
        const filtered = current.filter((value) => ids.has(extractUuid(value)));
        if (filtered.length === current.length) return current;
        return filtered.length ? filtered : null;
      };
    };

    const syncDependentFilters = async () => {
      if (productUuids.length === 0) {
        setOffersFilter((current) => (current?.length ? null : current));
        setCheckoutsFilter((current) => (current.length > 0 ? [] : current));
        return;
      }

      const [availableOffers, availableAffiliates, availableCoproducers] =
        await Promise.all([
          SellsService.getSoldOffers({ productIds: productUuids, currency }),
          SellsService.getSoldAffiliates({ productIds: productUuids }),
          SellsService.getSoldCoproducers({ productIds: productUuids }),
        ]);

      if (!active) return;

      setOffersFilter(keepAvailable(availableOffers));
      setAffiliatesFilter(keepAvailable(availableAffiliates));
      setCoproducersFilter(keepAvailable(availableCoproducers));

      if (offerUuids.length === 0) {
        setCheckoutsFilter((current) => (current.length > 0 ? [] : current));
        return;
      }

      const availableCheckouts = await SellsService.getSoldCheckouts({
        productIds: productUuids,
        offerIds: offerUuids,
        currency,
      });

      if (!active) return;

      const keepCheckouts = keepAvailable(availableCheckouts);
      setCheckoutsFilter((current) => keepCheckouts(current) ?? []);
    };

    syncDependentFilters();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productUuidsKey,
    offerUuidsKey,
    currency,
    setAffiliatesFilter,
    setCheckoutsFilter,
    setCoproducersFilter,
    setOffersFilter,
  ]);

  const dateRange = useMemo(
    () => ({
      from: fromDate || undefined,
      to: toDate || undefined,
    }),
    [fromDate, toDate],
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from) setFromDate(range.from);
      if (range?.to) setToDate(range.to);
    },
    [setFromDate, setToDate],
  );

  const startDate = (
    dateRange.from ? startOfDay(dateRange.from) : startOfDay(new Date())
  ).toISOString();
  const endDate = (
    dateRange.to ? endOfDay(dateRange.to) : endOfDay(dateRange.from ?? new Date())
  ).toISOString();

  const queryParams: AnalyticsQueryParams = useMemo(
    () => ({
      checkouts: checkoutsFilter.map(extractUuid),
      products: extractUuids(productsFilter) ?? null,
      offers: extractUuids(offersFilter) ?? null,
      affiliates: extractUuids(affiliatesFilter) ?? null,
      coproducers: extractUuids(coproducersFilter) ?? null,
      templates: extractUuids(templatesFilter) ?? null,
      role: role || "producer",
      startDate,
      endDate,
      currency,
    }),
    [
      checkoutsFilter,
      productsFilter,
      offersFilter,
      affiliatesFilter,
      coproducersFilter,
      templatesFilter,
      role,
      startDate,
      endDate,
      currency,
    ],
  );

  const queryKeyParams = [
    queryParams.checkouts,
    queryParams.products,
    queryParams.offers,
    queryParams.affiliates,
    queryParams.coproducers,
    queryParams.templates,
    queryParams.role,
    queryParams.startDate,
    queryParams.endDate,
    queryParams.currency,
  ];

  const [checkoutsPage, setCheckoutsPage] = useState(1);

  useEffect(() => {
    setCheckoutsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, queryKeyParams);

  const analyticsQuery = useQuery<AnalyticsResponse>({
    queryKey: ["analytics", ...queryKeyParams],
    queryFn: async () => {
      const apiParams = buildAnalyticsParams(queryParams);
      const [overview, funnels, charts] = await Promise.all([
        ReportsService.getAnalyticsOverview(apiParams),
        ReportsService.getAnalyticsFunnels(apiParams),
        ReportsService.getAnalyticsSalesCharts(apiParams),
      ]);
      return { overview, funnels, charts };
    },
    retry: false,
  });

  const checkoutsQuery = useQuery<AnalyticsCheckoutsResponse>({
    queryKey: ["analytics-checkouts", ...queryKeyParams, checkoutsPage, CHECKOUTS_PER_PAGE],
    queryFn: () =>
      ReportsService.getAnalyticsCheckouts(
        buildAnalyticsParams(queryParams, {
          page: checkoutsPage,
          perPage: CHECKOUTS_PER_PAGE,
        }),
      ),
    retry: false,
  });

  const applyFilterForm = useCallback(
    (data: FilterAnalyticsFormData) => {
      const nextRole = data.role || "producer";
      setRole(nextRole);
      localStorage.setItem(ANALYTICS_ROLE_STORAGE_KEY, nextRole);

      const nextCheckouts = toFilterValues(data.checkouts);
      setCheckoutsFilter(nextCheckouts?.length ? nextCheckouts : []);

      (Object.keys(filterSetters) as (keyof typeof filterSetters)[]).forEach((key) => {
        const next = toFilterValues(data[key]);
        filterSetters[key](next?.length ? next : null);
      });
    },
    [filterSetters, setCheckoutsFilter, setRole],
  );

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const currentValues = filterParams[key] as string[] | undefined;
      const newValues = currentValues?.filter((_, i) => i !== index);

      if (key === "checkouts") {
        setCheckoutsFilter(newValues?.length ? newValues : []);
        return;
      }

      const setter = filterSetters[key as keyof typeof filterSetters];
      setter?.(newValues?.length ? newValues : null);
    },
    [filterParams, filterSetters, setCheckoutsFilter],
  );

  const clearAllFilters = useCallback(async () => {
    const defaultRole = await resolveDefaultRole();
    setRole(defaultRole);
    localStorage.setItem(ANALYTICS_ROLE_STORAGE_KEY, defaultRole);
    setCheckoutsFilter([]);
    Object.values(filterSetters).forEach((setter) => setter(null));
  }, [filterSetters, resolveDefaultRole, setCheckoutsFilter, setRole]);

  const hasActiveFilters =
    role !== "producer" ||
    checkoutsFilter.length > 0 ||
    Boolean(productsFilter?.length) ||
    Boolean(offersFilter?.length) ||
    Boolean(affiliatesFilter?.length) ||
    Boolean(coproducersFilter?.length) ||
    Boolean(templatesFilter?.length);

  const keyLabel: KeyLabel = useMemo(
    () => ({
      role: t("reports:filter_view_as"),
      products: t("sales:product"),
      offers: t("sales:offer"),
      checkouts: t("common:select_checkout"),
      affiliates: t("sales:affiliate"),
      coproducers: t("sales:co_producer"),
      templates: t("reports:template"),
    }),
    [t],
  );

  const filterBadges: FilterBadge[] = useMemo(() => {
    const roleBadgeLabel: Record<string, string> = {
      producer: t("reports:role_producer"),
      affiliate: t("reports:role_affiliate"),
      coproducer: t("reports:role_coproducer"),
    };

    const badges: FilterBadge[] = [
      {
        key: "role",
        group: `${keyLabel.role}:`,
        label: roleBadgeLabel[role] || role,
        onClick: () => openModal(FILTER_MODAL_KEY),
      },
    ];

    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (key === "role" || !Array.isArray(values)) return;

      values.forEach((value, index) => {
        badges.push({
          key: `${key}-${index}`,
          group: `${keyLabel[key]}:`,
          label:
            typeof value === "string"
              ? value
              : `${value?.label || value?.value || value}`,
          onClick: () => removeFilterItem(key, index),
        });
      });
    });

    return badges;
  }, [role, filterVisualParams, keyLabel, openModal, removeFilterItem, t]);

  return {
    dateRange,
    handleDateRangeChange,
    filterValues: { role, ...filterVisualParams },
    applyFilterForm,
    clearAllFilters,
    hasActiveFilters,
    filterBadges,
    analytics: analyticsQuery.data,
    isLoadingAnalytics: analyticsQuery.isLoading,
    isFetchingAnalytics: analyticsQuery.isFetching,
    checkouts: checkoutsQuery.data,
    isLoadingCheckouts: checkoutsQuery.isLoading,
    isFetchingCheckouts: checkoutsQuery.isFetching,
    setCheckoutsPage,
  };
}
